import React from 'react';
import GeovisLogo from './GeovisLogo';
import { Link } from 'react-router-dom';
import { Mail, Phone, ArrowUpRight, Linkedin, Globe } from 'lucide-react';
import { LOCALES, LOCALE_ORDER } from '../data/locales';

export const Footer = ({ onOpenDemo }) => {
  return (
    /* Superficie nivel 0, más profunda que el canvas: el cierre de la página
       (patrón Felt "darkest base"), no una tarjeta flotando. */
    <footer
      className="mt-16 border-t border-border/60"
      style={{ background: 'oklch(var(--nivel0))' }}
    >
      <div className="mx-auto max-w-7xl px-6 py-12 sm:py-14 relative">
        {/* Coordenadas decorativas */}
        <div className="coords-decorator top-4 right-6 hidden sm:block">
          40.4168° N, 3.7038° W
        </div>

        {/* Grid de contenido */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Columna 1: Marca */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <Link to="/" className="hover:opacity-80 transition-opacity">
                <GeovisLogo className="h-8 w-auto" />
              </Link>
            </div>
            <p className="text-sm leading-relaxed">
              <span className="font-medium text-foreground/90">Encuentra, analiza y decide.</span>
            </p>
            <div className="mt-5 space-y-2">
              <a href="mailto:info@geovis.es" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group">
                <Mail className="w-4 h-4" />
                info@geovis.es
              </a>
              <a href="tel:+34606515482" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group">
                <Phone className="w-4 h-4" />
                +34 606 515 482
              </a>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <a
                data-testid="footer-linkedin-link"
                href="https://es.linkedin.com/company/geovis"
                target="_blank"
                rel="noreferrer"
                aria-label="GeoVis en LinkedIn"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border text-muted-foreground hover:text-primary-text hover:border-primary transition-colors"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Columna 2: Producto */}
          <div className="lg:col-span-1">
            <h3 className="text-[11px] font-medium tracking-wider text-[oklch(var(--ink-faint))] uppercase mb-4">Producto</h3>
            <div className="flex flex-col gap-3 text-sm">
              <Link to="/urbanismo" className="text-muted-foreground hover:text-foreground transition-colors">
                Urbanismo
              </Link>
              <Link to="/urbanismo/metodologia" className="text-muted-foreground hover:text-foreground transition-colors">
                Metodología
              </Link>
              <Link to="/urbanismo/glosario" className="text-muted-foreground hover:text-foreground transition-colors">
                Glosario urbanístico
              </Link>
              <Link to="/territorio" className="text-muted-foreground hover:text-foreground transition-colors">
                Territorio · visor GIS
              </Link>
              <Link to="/renovables" className="text-muted-foreground hover:text-foreground transition-colors">
                Renovables
              </Link>
              <Link to="/informe" className="text-muted-foreground hover:text-foreground transition-colors">
                Informe de viabilidad
              </Link>
            </div>
          </div>

          {/* Columna 3: Empresa */}
          <div className="lg:col-span-1">
            <h3 className="text-[11px] font-medium tracking-wider text-[oklch(var(--ink-faint))] uppercase mb-4">Empresa</h3>
            <div className="flex flex-col gap-3 text-sm">
              <Link data-testid="footer-blog-link" to="/blog" className="text-muted-foreground hover:text-foreground transition-colors">
                Blog
              </Link>
              <Link to="/demo" className="text-muted-foreground hover:text-foreground transition-colors">
                Solicitar demo
              </Link>
            </div>
          </div>

          {/* Columna 4: Legal */}
          <div className="lg:col-span-1">
            <h3 className="text-[11px] font-medium tracking-wider text-[oklch(var(--ink-faint))] uppercase mb-4">Legal</h3>
            <div className="flex flex-col gap-3 text-sm">
              <Link data-testid="footer-legal-link" to="/aviso-legal" className="text-muted-foreground hover:text-foreground transition-colors">
                Aviso legal
              </Link>
              <Link data-testid="footer-privacy-link" to="/privacidad" className="text-muted-foreground hover:text-foreground transition-colors">
                Privacidad
              </Link>
              <Link data-testid="footer-terms-link" to="/terminos" className="text-muted-foreground hover:text-foreground transition-colors">
                Términos y Condiciones
              </Link>
            </div>
          </div>
        </div>

        {/* Locale switcher */}
        <div className="mt-10 pt-6 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[11px] font-medium tracking-wider text-[oklch(var(--ink-faint))] uppercase">Países</span>
          </div>
          <ul className="flex flex-wrap gap-2 text-sm">
            {LOCALE_ORDER.map((code) => {
              const l = LOCALES[code];
              const isEarly = l.earlyAccess === true;
              return (
                <li key={code}>
                  <Link
                    to={l.path}
                    hrefLang={l.hreflang}
                    data-testid={`footer-locale-${code}`}
                    className="inline-flex items-center gap-2 rounded-sm border border-border bg-card/50 hover:bg-card/80 hover:border-primary/40 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span>{l.countryName}</span>
                    {isEarly && (
                      <span className="text-[10px] uppercase tracking-wider text-[oklch(var(--proximo))] font-medium">
                        Acceso anticipado
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Footer bottom */}
        <div className="mt-10 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <span data-testid="footer-copyright">© 2026 GeoVis. Todos los derechos reservados.</span>
          <span className="flex items-center gap-1">
            Desarrollado por{' '}
            <a
              data-testid="footer-trazados-link"
              href="https://www.trazados.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[oklch(var(--primary-text))] hover:text-[oklch(var(--primary-text)/0.8)] transition-colors font-medium"
            >
              Trazados
              <ArrowUpRight className="w-3 h-3" />
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
